import React from 'react';
import { Tooltip } from 'antd';
import styled from 'styled-components';

import ConfidenceBadge from 'components/Intelligence/ConfidenceBadge';
import ConfidenceReason from 'components/Intelligence/ConfidenceReason';
import { intelligenceScopeDeclarations } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { OrbitAnswerConfidence } from '../answerConfidence';
import { formatMetricValue } from '../chartAdapters';
import useOrbitGrowthPlannerHandoff from '../handoff/useOrbitGrowthPlannerHandoff';
import { TOrbit } from '../i18n/TModules';
import { orbitSpace, orbitTokens, orbitType, perx3OrbitPrimaryButton } from '../styles';
import { OrbitTurn } from '../types';
import { OrbitOutcomeCard } from './OrbitHandoffCard';

export interface IOrbitAnswerFooterProps {
  /** The turn's confidence reading. The footer is not drawn at all without one. */
  confidence: OrbitAnswerConfidence;
  /** The answered turn itself: the handoff gate is read off its governed metric results. */
  turn: OrbitTurn;
}

/*
  The reading is one line with its reason UNDER it (user, 2026-09-14), so the drawn pill/button
  band becomes the first row of a column rather than the whole footer.

  The token declarations are what make the shared pill a pill here. `ConfidenceBadge` and
  `ConfidenceReason` draw their fill, stroke, ink, padding and type from `tokens.*`, which are
  `var(--is-*)` references declared only on `IntelligenceThemeScope`; the Orbit panel mounts outside
  every Intelligence route, so each of those declarations was invalid at computed-value time and the
  badge collapsed to inherited plain text. Redeclaring the same token layer on this element is the
  call `BreakdownModal` and `GrowthPlannerSlider` already make for the same reason.
*/
const Root = styled.div<{ $perx3: boolean }>`
  ${({ $perx3 }) => intelligenceScopeDeclarations($perx3)}

  /* PERX3-REDESIGN round 6: the Reason line sits 8px under the reading it explains. */
  > * + * {
    margin-top: ${orbitSpace('space-reason', '6px')};
  }
`;

/**
 * span `5032:10916` / `5017:9902` and BUTTON `5032:10921` / `5017:9917` share a band at y 494-558
 * of the two governed-analysis panels: the pill sits at the card's left inset and the button at
 * 293, against a content column that ends at 470. They are one row here rather than two absolutely
 * placed children, so a wrapped pill pushes the button down instead of under it.
 */
const Row = styled.div`
  /* PERX3-REDESIGN round 6: the confidence/CTA band is given air on both sides under the flag; with
     it off the band carries no margin of its own and the card's rhythm places it, as drawn. */
  margin: ${orbitSpace('space-cta', '0px')} 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 12px;
  }
`;

// The CTA and its help glyph travel together, so a wrapped row never leaves the "?" behind.
const CtaGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 8px;
  }
`;

/*
  PLA-8844: Orbit's bespoke confidence pill is gone. `ConfidenceBadge` is the reading Customer
  Insights and Customer 360 already draw (PLA-8815 / PLA-8828) — "High confidence · 91%", or the
  band word alone where the wire carries no score — and `ConfidenceReason` is the "Reason: …" line
  under it. Both are reused UNCHANGED, and both resolve their `var(--is-*)` tokens off `Root` above.

  One consequence is carried over deliberately rather than forked away: the badge inks the high band
  `#16834F` on `#15382D`, 2.69:1, where Orbit's own pill had lifted that ink to the stroke colour
  `#91D5AD`. The reading now reads IDENTICALLY to the other two screens, which is what was asked
  for; the contrast is the shared component's to fix, not Orbit's to diverge on again.
*/

/**
 * BUTTON `5032:10921`: 158 x 29, r8, `#4F46E5`, label Inter 13 white and centred.
 *
 * 158 is a MINIMUM. The frame's own label `5032:10922` measures 163.69 and starts 3 left of the 158
 * button, so the drawn box cannot hold the drawn string: shipped as a fixed width it wrapped to two
 * 15px lines inside a 29px box and overflowed it. Hugging past 158 keeps the drawn size wherever
 * the label fits and keeps the button one line where it does not.
 */
const SendButton = styled.button<{ $perx3: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 158px;
  height: 29px;
  padding: 0 8px;
  white-space: nowrap;
  border: none;
  border-radius: 8px;
  background: ${orbitTokens.actionStrong};
  color: ${orbitTokens.onAction};
  cursor: pointer;
  font-family: inherit;
  ${orbitType.action}
  font-weight: 400;

  &:focus-visible {
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 2px;
  }

  /*
    The inert state keeps the DRAWN fill. It shipped darkened to 2E2D86 to read as unavailable, but
    the drawn 4F46E5 carries the white label at 6.29:1 and the darkened wash was on no deviation
    list, so the fill returns to the frame's and the cursor plus aria-disabled carry the state. Not
    opacity: the label is reachable by keyboard (aria-disabled, not disabled), so it is not an
    inactive control and the 4.5:1 floor still applies.
  */
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }

  /* Round 4 (user): the Growth Planner handoff is a primary button, so it takes the redesign's
     black treatment. The hover in that block excludes aria-disabled, which this one always
     carries, so the inert state keeps its resting fill. */
  ${({ $perx3 }) => $perx3 && perx3OrbitPrimaryButton}
`;

/**
 * The help affordance FR §4.14 asks for beside the CTA (PLA-9563 §4c). A real button rather than a
 * bare glyph with a `title`, so it is reachable by keyboard and announced; the bubble is mounted
 * inside this row, which keeps it inside the panel's own clip instead of at the document root.
 */
const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 1px solid ${orbitTokens.border};
  border-radius: 50%;
  background: transparent;
  color: ${orbitTokens.textSecondary};
  cursor: help;
  font-family: inherit;
  ${orbitType.helping}
  font-weight: 700;

  &:hover,
  &:focus-visible {
    border-color: ${orbitTokens.action};
    color: ${orbitTokens.action};
    outline: none;
  }
`;

// Announced on focus; the button's own label cannot carry the reason without changing the drawn copy.
const HiddenReason = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  overflow: hidden;
  white-space: nowrap;
`;

const REASON_ID = 'orbit-growth-planner-reason';

/**
 * The confidence reading and the Growth Planner handoff, under an answered turn's analysis card.
 * Drawn on the two governed-analysis panels (`5017:9498` Desktop-11 and `5032:10507` Desktop-13).
 */
const OrbitAnswerFooter: React.FC<IOrbitAnswerFooterProps> = ({ confidence, turn }) => {
  const { t } = useMultiLanguageTranslation();
  const isPerx3 = usePerx3Redesign();
  const { gate, status, receipt, errorRef, send } = useOrbitGrowthPlannerHandoff({ turn });

  /*
    PLA-9563 §4a. The CTA used to be drawn on every answered turn that carried any confidence at
    all, including metrics the payload had marked ineligible. It now appears only where the server
    said so — `confidence.handoff_ok === true` — with the one exception the reader is allowed to be
    told about: a restricted TURN keeps the button, inert, so the restriction is explained. PLA-9579:
    that exception is read off this turn's own outcome, never off a session-wide flag.
  */
  const restricted = gate.blocker === 'restricted';
  const drawsCta = gate.eligible || restricted;
  const sending = status === 'sending';
  const inert = restricted || sending;
  const reason = restricted
    ? t(`${TOrbit}.actions.growthPlannerRestricted`)
    : t(`${TOrbit}.actions.growthPlannerSending`);

  const renderOutcome = (): React.ReactNode => {
    if (status === 'success') {
      return (
        <OrbitOutcomeCard
          top={t(`${TOrbit}.handoff.sentTop`)}
          body={t(`${TOrbit}.handoff.sentBody`)}
          steps={[
            t(`${TOrbit}.handoff.sentMetrics`, { count: gate.metricIds.length }),
            t(`${TOrbit}.handoff.sentCustomers`, {
              count: gate.customers,
              total: formatMetricValue(gate.customers)
            }),
            // PLA-9579: bands with no stated cohort size are excluded from the figure above rather
            // than counted as 0, so the reader is told when that figure is a floor.
            ...(gate.populationPartial ? [t(`${TOrbit}.handoff.sentPartial`)] : []),
            t(`${TOrbit}.handoff.sentReference`, { reference: receipt?.handoffId ?? '' })
          ]}
          testId="orbit-growth-planner-result"
          topTestId="orbit-growth-planner-result-top"
          stepTestId="orbit-growth-planner-result-step"
        />
      );
    }

    if (status === 'blocked' || status === 'unavailable') {
      return (
        <OrbitOutcomeCard
          tone="negative"
          top={t(`${TOrbit}.handoff.${status === 'blocked' ? 'blockedTop' : 'unavailableTop'}`)}
          body={
            status === 'blocked'
              ? t(`${TOrbit}.handoff.blockedBody`)
              : t(`${TOrbit}.handoff.unavailableBody`, { reference: errorRef ?? '' })
          }
          testId="orbit-growth-planner-result"
          topTestId="orbit-growth-planner-result-top"
          stepTestId="orbit-growth-planner-result-step"
        />
      );
    }

    return null;
  };

  return (
    <Root $perx3={isPerx3} data-testid="orbit-answer-footer">
      <Row>
        <ConfidenceBadge band={confidence.band} score={confidence.score} />
        {drawsCta ? (
          <CtaGroup>
            {/*
              `aria-disabled` rather than the native attribute, the same call GrowthPlannerCta makes:
              `disabled` drops the control out of the tab order, so the one thing this state has to
              say — why it is unavailable — can never be reached.
            */}
            <SendButton
              type="button"
              aria-busy={sending}
              aria-disabled={inert}
              aria-describedby={inert ? REASON_ID : undefined}
              data-state={restricted ? 'restricted' : status}
              data-testid="orbit-answer-growth-planner"
              onClick={inert ? undefined : send}
              $perx3={isPerx3}
            >
              {t(`${TOrbit}.actions.growthPlanner`)}
            </SendButton>
            <Tooltip
              autoAdjustOverflow
              getPopupContainer={trigger => trigger.parentElement ?? document.body}
              placement="topRight"
              title={t(`${TOrbit}.actions.growthPlannerHelp`)}
              trigger={['hover', 'focus', 'click']}
            >
              <HelpButton
                type="button"
                aria-label={t(`${TOrbit}.actions.growthPlannerHelpLabel`)}
                data-testid="orbit-answer-growth-planner-help"
              >
                ?
              </HelpButton>
            </Tooltip>
            {inert ? <HiddenReason id={REASON_ID}>{reason}</HiddenReason> : null}
          </CtaGroup>
        ) : null}
      </Row>
      <ConfidenceReason reasons={confidence.reasons} />
      {renderOutcome()}
    </Root>
  );
};

export default OrbitAnswerFooter;
